import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Radio,
  Select,
  Statistic,
  Table,
  Tag,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DownloadOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { fetchCategories } from "store/slices/categorySlice";
import {
  createMasterProduct,
  deleteMasterProduct,
  fetchMasterProducts,
  updateMasterProduct,
} from "store/slices/masterProductsSlice";
import MasterProductFormModal from "./components/MasterProductFormModal";
import MasterProductDetailModal from "./components/MasterProductDetailModal";
import DeleteMasterProductModal from "./components/DeleteMasterProductModal";
import { generateProductCode } from "./utils";
import type {
  MasterProduct,
  MasterProductPayload,
  MasterProductStatusFilter,
} from "./types";

import { ALLOWED_UNITS } from "shared/constants/units";

interface MasterProductsPageProps {}

type CsvImportRow = {
  key: number;
  rowNumber: number;
  productName: string;
  productCode: string;
  categoryName: string;
  categoryId?: number;
  unit: string;
  isActive: boolean;
  statusLabel: string;
  errors: string[];
};

const allowedUnits = ALLOWED_UNITS;

const csvTemplateRows = [
  ["product_name", "category_name", "unit", "is_active"],
  ["Basmati Rice", "Grains", "kg", "true"],
  ["Fresh Apple", "Fruits", "kg", "true"],
  ["Palm Oil", "Oil", "liter", "true"],
  ["Red Onion", "Vegetables", "kg", "true"],
];

const SHOW_CSV_IMPORT = false;

const escapeCsvValue = (value: string) => {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
};

const parseCsvText = (text: string) => {
  const rows: string[][] = [];
  let current = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === "," && !inQuotes) {
      row.push(current);
      current = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }
      row.push(current);
      if (row.some((cell) => cell.trim() !== "")) {
        rows.push(row);
      }
      row = [];
      current = "";
      continue;
    }

    current += char;
  }

  row.push(current);
  if (row.some((cell) => cell.trim() !== "")) {
    rows.push(row);
  }

  return rows;
};

const normalizeHeader = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, "_");

const parseStatusValue = (value: string) => {
  const normalized = value.trim().toLowerCase();

  if (!normalized) return { value: true, valid: true };
  if (["true", "1", "yes", "active"].includes(normalized)) {
    return { value: true, valid: true };
  }
  if (["false", "0", "no", "inactive"].includes(normalized)) {
    return { value: false, valid: true };
  }

  return { value: true, valid: false };
};

// TODO: Replace local state with future APIs when available:
// GET /master-products, POST /master-products, GET /master-products/{id},
// PUT /master-products/{id}, DELETE /master-products/{id}.
// TODO: Use POST /master-products/bulk when backend bulk import is available.
// TODO: Later support vendor product requests:
// GET /master-product-requests,
// PATCH /master-product-requests/{id}/approve,
// PATCH /master-product-requests/{id}/reject.
export const MasterProductsPage: React.FunctionComponent<
  MasterProductsPageProps
> = () => {
  const dispatch = useAppDispatch();
  const { categories, error: categoriesError } = useAppSelector(
    (state) => state.categories
  );
  const {
    products,
    loading: productsLoading,
    actionLoading,
    error: productsError,
  } = useAppSelector((state) => state.masterProducts);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<MasterProductStatusFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<number | "all">("all");
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [csvRows, setCsvRows] = useState<CsvImportRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchMasterProducts());
  }, [dispatch]);

  const editingProduct = useMemo(
    () => products.find((product) => product.id === editingId) ?? null,
    [products, editingId]
  );

  const viewingProduct = useMemo(
    () => products.find((product) => product.id === viewingId) ?? null,
    [products, viewingId]
  );

  const deletingProduct = useMemo(
    () => products.find((product) => product.id === deletingId) ?? null,
    [products, deletingId]
  );

  const categoryOptions = useMemo(
    () => [
      { label: "All Categories", value: "all" as const },
      ...categories.map((category) => ({
        label: category.name,
        value: category.id,
      })),
    ],
    [categories]
  );

  const filteredProducts = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return products.filter((product) => {
      const matchesSearch =
        !keyword ||
        product.name.toLowerCase().includes(keyword) ||
        product.product_code.toLowerCase().includes(keyword) ||
        (product.category_name || "").toLowerCase().includes(keyword);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && product.is_active) ||
        (statusFilter === "inactive" && !product.is_active);
      const matchesCategory =
        categoryFilter === "all" || product.category_id === categoryFilter;

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [products, search, statusFilter, categoryFilter]);

  const openAdd = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = (id: number) => {
    setEditingId(id);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
  };

  const downloadCsvTemplate = () => {
    const csv = csvTemplateRows
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "master-products-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildCsvPreviewRows = (text: string): CsvImportRow[] => {
    const rows = parseCsvText(text);
    const [headers = [], ...dataRows] = rows;
    const headerMap = headers.reduce<Record<string, number>>(
      (accumulator, header, index) => {
        accumulator[normalizeHeader(header)] = index;
        return accumulator;
      },
      {}
    );
    const categoryByName = new Map(
      categories.map((category) => [category.name.trim().toLowerCase(), category])
    );
    const existingCodes = new Set(
      products.map((product) => product.product_code.trim().toUpperCase())
    );
    const csvCodeCounts = new Map<string, number>();

    const previewRows = dataRows.map((row, index) => {
      const productName = (row[headerMap.product_name] || "").trim();
      const productCode = generateProductCode(productName);
      csvCodeCounts.set(productCode, (csvCodeCounts.get(productCode) || 0) + 1);

      return {
        key: index + 2,
        rowNumber: index + 2,
        productName,
        productCode,
        categoryName: (row[headerMap.category_name] || "").trim(),
        unit: (row[headerMap.unit] || "").trim().toLowerCase(),
        rawStatus: (row[headerMap.is_active] || "").trim(),
      };
    });

    return previewRows.map((row) => {
      const errors: string[] = [];
      const category = categoryByName.get(row.categoryName.toLowerCase());
      const status = parseStatusValue(row.rawStatus);

      if (!row.productName) {
        errors.push("Product name is required.");
      }
      if (!row.productCode) {
        errors.push("Product code could not be generated.");
      }
      if (row.productCode && (csvCodeCounts.get(row.productCode) || 0) > 1) {
        errors.push("Duplicate product code inside CSV.");
      }
      if (row.productCode && existingCodes.has(row.productCode)) {
        errors.push("Duplicate product code already exists.");
      }
      if (!row.categoryName) {
        errors.push("Category is required.");
      } else if (!category) {
        errors.push("Category not found.");
      }
      if (!allowedUnits.has(row.unit)) {
        errors.push("Invalid unit.");
      }
      if (!status.valid) {
        errors.push("Invalid status value.");
      }

      return {
        key: row.key,
        rowNumber: row.rowNumber,
        productName: row.productName,
        productCode: row.productCode,
        categoryName: category?.name || row.categoryName,
        categoryId: category?.id,
        unit: row.unit,
        isActive: status.value,
        statusLabel: status.value ? "Active" : "Inactive",
        errors,
      };
    });
  };

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      message.error("Please select a CSV file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || "");
        const rows = buildCsvPreviewRows(text);
        setCsvRows(rows);
        setCsvPreviewOpen(true);
      } catch {
        message.error("Unable to parse CSV file.");
      }
    };
    reader.onerror = () => message.error("Unable to read CSV file.");
    reader.readAsText(file);
  };

  const importValidCsvRows = () => {
    const validRows = csvRows.filter((row) => row.errors.length === 0);

    if (validRows.length === 0) {
      message.error("No valid rows to import.");
      return;
    }

    setCsvRows([]);
    setCsvPreviewOpen(false);
    message.info("CSV import is hidden until bulk API support is enabled.");
  };

  const saveProduct = async (payload: MasterProductPayload) => {
    if (editingId && editingProduct) {
      try {
        await dispatch(updateMasterProduct({ id: editingId, payload })).unwrap();
        message.success("Master product updated.");
        closeForm();
      } catch (error) {
        message.error(
          typeof error === "string" ? error : "Unable to update master product."
        );
      }
      return;
    }

    try {
      await dispatch(createMasterProduct(payload)).unwrap();
      message.success("Master product added.");
      closeForm();
    } catch (error) {
      message.error(
        typeof error === "string" ? error : "Unable to create master product."
      );
    }
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    const name = deletingProduct?.name;

    try {
      await dispatch(deleteMasterProduct(deletingId)).unwrap();
      setDeletingId(null);
      if (viewingId === deletingId) {
        setDetailOpen(false);
        setViewingId(null);
      }
      message.success(`Master product${name ? ` (${name})` : ""} deleted.`);
    } catch (error) {
      message.error(
        typeof error === "string" ? error : "Unable to delete master product."
      );
    }
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const columns: ColumnsType<MasterProduct> = [
    {
      title: "Product Code",
      dataIndex: "product_code",
      key: "product_code",
      render: (code?: string) => (
        <span className="master-product-code">{code || "—"}</span>
      ),
      width: 190,
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      render: (category?: string) => category || "—",
    },
    {
      title: "Unit",
      dataIndex: "default_unit",
      key: "default_unit",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "success" : "default"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDate,
      width: 150,
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, product) => (
        <div className="master-product-table-actions">
          <Button
            type="text"
            size="small"
            className="market-card-btn-view"
            onClick={() => {
              setViewingId(product.id);
              setDetailOpen(true);
            }}
          >
            View
          </Button>
          <Button
            type="text"
            size="small"
            className="market-card-btn-edit"
            onClick={() => openEdit(product.id)}
          >
            Edit
          </Button>
          <Button
            type="text"
            size="small"
            className="market-card-btn-delete"
            onClick={() => setDeletingId(product.id)}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  const csvValidCount = csvRows.filter((row) => row.errors.length === 0).length;
  const csvErrorCount = csvRows.length - csvValidCount;
  const csvPreviewColumns: ColumnsType<CsvImportRow> = [
    {
      title: "Row",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 80,
    },
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      render: (value?: string) => value || "—",
    },
    {
      title: "Product Code",
      dataIndex: "productCode",
      key: "productCode",
      render: (value?: string) => (
        <span className="master-product-code">{value || "—"}</span>
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      key: "categoryName",
      render: (value?: string) => value || "—",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      render: (value?: string) => value || "—",
    },
    {
      title: "Status",
      dataIndex: "statusLabel",
      key: "statusLabel",
      width: 110,
      render: (_: string, row) => (
        <Tag color={row.isActive ? "success" : "default"}>
          {row.statusLabel}
        </Tag>
      ),
    },
    {
      title: "Validation Status",
      dataIndex: "errors",
      key: "errors",
      render: (errors: string[]) =>
        errors.length === 0 ? (
          <Tag color="success">Valid</Tag>
        ) : (
          <div className="master-product-csv-errors">
            {errors.map((error) => (
              <Tag color="error" key={error}>
                {error}
              </Tag>
            ))}
          </div>
        ),
    },
  ];

  return (
    <React.Fragment>
      <CmsTopBar title="Master Products" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="markets-section-header mb-3">
            <h2 className="markets-heading">
              Product Library ({filteredProducts.length})
            </h2>
            <div className="master-product-header-actions">
              {SHOW_CSV_IMPORT && (
                <>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={downloadCsvTemplate}
                  >
                    Download CSV Template
                  </Button>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Import CSV
                  </Button>
                </>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="markets-add-btn"
                onClick={openAdd}
              >
                Add Master Product
              </Button>
            </div>
            {SHOW_CSV_IMPORT && (
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="master-product-csv-input"
                onChange={handleCsvFileChange}
              />
            )}
          </div>

          <div className="master-product-info-banner">
            <InfoCircleOutlined />
            <span>
              Master Products are the shared catalog used for vendor price
              comparison. Vendors select from this catalog instead of creating
              free-text products.
            </span>
          </div>

          {categoriesError && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={categoriesError}
            />
          )}

          {productsError && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={productsError}
            />
          )}

          <Row className="mb-3 gy-2">
            <Col xs={12} lg={5}>
              <Input
                placeholder="Search master products"
                prefix={<SearchOutlined className="markets-search-icon" />}
                className="markets-search markets-search-full"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
            <Col xs={12} lg={4}>
              <Radio.Group
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="package-filter-group master-product-status-filter"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="active">Active</Radio.Button>
                <Radio.Button value="inactive">Inactive</Radio.Button>
              </Radio.Group>
            </Col>
            <Col xs={12} lg={3}>
              <Select
                value={categoryFilter}
                options={categoryOptions}
                className="master-product-category-filter"
                onChange={(value) => setCategoryFilter(value)}
              />
            </Col>
          </Row>

          <div
            className={`master-product-list-wrap ${
              !productsLoading && filteredProducts.length === 0
                ? "master-product-list-wrap-empty"
                : ""
            }`}
          >
            {!productsLoading && filteredProducts.length === 0 ? (
              <Empty description="No master products found." />
            ) : (
              <Table
                rowKey="id"
                columns={columns}
                dataSource={filteredProducts}
                className="master-products-table"
                size="middle"
                loading={productsLoading}
                scroll={{ x: 860 }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  hideOnSinglePage: filteredProducts.length <= 10,
                }}
              />
            )}
          </div>
        </Container>
      </Container>

      <MasterProductFormModal
        open={formOpen}
        mode={editingId ? "edit" : "add"}
        initialProduct={editingProduct}
        submitting={actionLoading}
        onClose={closeForm}
        onSubmit={saveProduct}
      />

      <MasterProductDetailModal
        open={detailOpen}
        product={viewingProduct}
        onClose={() => {
          setDetailOpen(false);
          setViewingId(null);
        }}
      />

      <DeleteMasterProductModal
        open={!!deletingId}
        productName={deletingProduct?.name}
        loading={actionLoading}
        onClose={() => setDeletingId(null)}
        onConfirm={confirmDelete}
      />

      {SHOW_CSV_IMPORT && (
        <Modal
          title="CSV Import Preview"
          open={csvPreviewOpen}
          onCancel={() => {
            setCsvPreviewOpen(false);
            setCsvRows([]);
          }}
          centered
          width={1100}
          className="modal-round master-product-csv-modal"
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setCsvPreviewOpen(false);
                setCsvRows([]);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="import"
              type="primary"
              className="markets-add-btn"
              disabled={csvValidCount === 0}
              onClick={importValidCsvRows}
            >
              Import Valid Products
            </Button>,
          ]}
        >
          <div className="master-product-csv-summary">
            <Statistic title="Total Rows" value={csvRows.length} />
            <Statistic title="Valid Rows" value={csvValidCount} />
            <Statistic title="Error Rows" value={csvErrorCount} />
          </div>

          <Table
            rowKey="key"
            columns={csvPreviewColumns}
            dataSource={csvRows}
            size="small"
            className="master-product-csv-table"
            rowClassName={(row) =>
              row.errors.length > 0 ? "master-product-csv-invalid-row" : ""
            }
            scroll={{ x: 980, y: 420 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              hideOnSinglePage: csvRows.length <= 10,
            }}
          />
        </Modal>
      )}
    </React.Fragment>
  );
};

export default MasterProductsPage;
